import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

/**
 * Formats a booking code for display.
 * Adds a hash prefix and a hyphen after the first 5 digits.
 *
 * @param value - The raw booking code string (e.g. "1234567890")
 * @returns The formatted booking code (e.g. "#12345-67890")
 */
export const formatBookingCode = (value: string) => {
  if (!value) return '';
  // If the value already contains a hyphen or doesn't match the expected pattern, return it as is (with # prefix if needed)
  // The user's regex replaces the first 5 digits with those digits followed by a hyphen.
  // Assuming the booking code is a string of digits.
  return `#${value.replace(/(\d{5})/, '$1-')}`;
};

export const formatPrice = (value?: number): string => {
  return `฿${(value ?? 0).toLocaleString()}`;
};

export function formatAppointmentDate(availabilityId?: string): string {
  if (!availabilityId) return '';

  try {
    const dateTime = dayjs(availabilityId);
    if (!dateTime.isValid()) return '';
    return dateTime.format('DD/MM/YYYY');
  } catch (error) {
    console.error('Error formatting appointment date:', error);
    return '';
  }
}

interface FormatAppointmentTimeRangeParams {
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  maxDurationMinutes: number;
  hoursShortLabel: string;
  minuteLabel: string;
}

export function formatAppointmentTimeRange({
  startTime,
  endTime,
  maxDurationMinutes,
  hoursShortLabel,
  minuteLabel,
}: FormatAppointmentTimeRangeParams): string {
  if (!startTime) return '';

  // If no treatments assigned yet (maxDurationMinutes === 0) we only show the start time.
  if (maxDurationMinutes === 0) {
    return startTime.format('HH:mm');
  }

  if (!endTime) return '';

  const timeRange = `${startTime.format('HH:mm')}-${endTime.format('HH:mm')}`;

  const hours = Math.floor(maxDurationMinutes / 60);
  const minutes = maxDurationMinutes % 60;

  const durationText =
    maxDurationMinutes >= 60
      ? `${hours} ${hoursShortLabel}${minutes > 0 ? ` ${minutes} ${minuteLabel}` : ''}`
      : `${maxDurationMinutes} ${minuteLabel}`;

  return `${timeRange} (${durationText})`;
}
